import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ConfigurationService,
  ContextService,
} from '@maximizer/core/shared/data-access';
import {
  ConfigurationSettingCodes,
  Octopus,
} from '@maximizer/core/shared/domain';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { parseISO } from 'date-fns';
import {
  AbEntryDetailsMapper,
  AbEntryEditMapper,
  AbEntryFieldOption,
  AbEntryFieldOptionMapper,
  AbEntryLastContactDatePreference,
  AbEntrySearchMapper,
  EmailEntryType,
  EntryType,
  OutlookAbEntryDetails,
  OutlookAbEntryForm,
  OutlookAbEntrySearch,
} from '@maximizer/outlook/shared/domain';
import { escapeCharacters } from '@maximizer/shared/util';
import { mapTextValuesToEnum } from '../utils/enum.util';
import { OutlookStore } from '../state/outlook.store';

@Injectable()
export class AbEntryService {
  private readonly lastContactedDatePreference: ConfigurationSettingCodes = {
    code3: 'AB_LAST_CONTACT_DATE_UPDATE',
  };

  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
    private readonly configurationService: ConfigurationService,
    private readonly outlookStore: OutlookStore,
  ) {}

  getByKey(key: string): Observable<OutlookAbEntrySearch | null> {
    const query: Octopus.Query<Octopus.AbEntry> = {
      Key: {
        $EQ: { Value: key },
      },
    };
    const request = this.getRequest(query);

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new AbEntrySearchMapper();
            return result.AbEntry.Data.map((abEntry) => mapper.from(abEntry));
          }
          return [];
        }),
      )
      .pipe(map((abEntries) => (abEntries.length ? abEntries[0] : null)));
  }

  getAllDetailsByKey(key: string): Observable<OutlookAbEntryDetails | null> {
    const query: Octopus.Query<Octopus.AbEntry> = {
      Key: {
        $EQ: { Value: key },
      },
    };

    const request = this.getRequest(query);

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new AbEntryDetailsMapper();
            const abentryMap = result.AbEntry.Data.map((abEntry) =>
              mapper.from(abEntry),
            );
            return abentryMap.length > 0 ? abentryMap[0] : null;
          }
          return null;
        }),
      );
  }

  getByEmails(emails: string[]): Observable<OutlookAbEntryDetails[]> {
    const searchEscapeCharacters = '-:[]';
    const searchQuery: Octopus.LogicalQuery<Octopus.AbEntry> = {
      $OR: emails.map((email) => ({
        $PHRASE: escapeCharacters(email, searchEscapeCharacters),
      })),
    };

    const request = this.getSmallRequest(searchQuery);

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(map((result) => this.mapAbEntryDetailsList(result)));
  }

  getByKeys(keys: string[]): Observable<OutlookAbEntryDetails[]> {
    const searchQuery: Octopus.LogicalQuery<Octopus.AbEntry> = {
      $OR: [],
    };
    for (const key of keys) {
      const query: Octopus.Query<Octopus.AbEntry> = {
        Key: { $EQ: key },
      };
      searchQuery.$OR?.push(query);
    }

    const request = this.getSmallRequest(searchQuery);

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(map((result) => this.mapAbEntryDetailsList(result)));
  }

  update(query: OutlookAbEntryForm): Observable<boolean> {
    const mapper = new AbEntryEditMapper();
    const request: Octopus.AbEntryWriteRequest = mapper.from(query);
    return this.http
      .post<Octopus.AbEntryWriteResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.Code === Octopus.ResponseStatusCode.Successful;
          }
          throw new Error('Unsuccessful request: ' + result?.Code);
        }),
      );
  }

  save(query: OutlookAbEntryForm): Observable<string | null> {
    const mapper = new AbEntryEditMapper();
    const request: Octopus.AbEntryWriteRequest = mapper.from(query);
    return this.http
      .post<Octopus.AbEntryWriteResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data.Key ?? '';
          }
          throw new Error('Failed to save abentry');
        }),
      );
  }

  saveCompany(companyName: string): Observable<string | null> {
    const request: Octopus.AbEntryWriteRequest = {
      AbEntry: {
        Data: {
          Key: null,
          CompanyName: companyName,
          Type: 'Company',
        },
      },
    };
    return this.http
      .post<Octopus.AbEntryWriteResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data.Key ?? '';
          }
          throw new Error('Failed to create company');
        }),
      );
  }

  getLastContactDate(key: string): Observable<Date | null> {
    const query: Octopus.Query<Octopus.AbEntry> = {
      Key: {
        $EQ: { Value: key },
      },
    };

    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: query,
        },
        Scope: {
          Fields: {
            Key: 1,
            LastContactDate: {
              Value: 1,
            },
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const abEntry = result.AbEntry.Data[0];
            if (abEntry?.LastContactDate?.Value) {
              return parseISO(abEntry.LastContactDate.Value);
            }
          }
          return null;
        }),
      );
  }

  getLastContactedPreferences(): Observable<
    AbEntryLastContactDatePreference[]
  > {
    return this.configurationService
      .getSetting(this.lastContactedDatePreference)
      .pipe(
        map((setting) =>
          mapTextValuesToEnum(
            setting?.textValue ?? '',
            AbEntryLastContactDatePreference,
          ),
        ),
        catchError((error) => {
          console.error('Error getting preferences:', error);
          return of([]);
        }),
      );
  }

  updateLastContactedDate(key: string, date: Date): Observable<boolean> {
    const request = {
      AbEntry: {
        Data: {
          Key: key,
          LastContactDate: date.toISOString(),
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.AbEntryWriteResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.Code === Octopus.ResponseStatusCode.Successful;
          }
          throw new Error('Unsuccessful request: ' + result?.Code);
        }),
      );
  }

  updateLastContactDateWithPreferences(
    entryId: string,
    entryType: EmailEntryType,
    preference: AbEntryLastContactDatePreference,
    date: Date,
    abEntryType?: string,
    parentKey?: string,
  ): Observable<Date | null> {
    const preferences = this.outlookStore.lastContactDatePreferences();
    const shouldUpdateEntryLastContactDate = preferences.includes(preference);
    const shouldUpdateParentLastContactDate = preferences.includes(
      AbEntryLastContactDatePreference.UpdateContactParentEntry,
    );

    // Last contact date must never be set past now
    const now = new Date();
    const effectiveDate = date > now ? now : date;

    const updateEntry =
      shouldUpdateEntryLastContactDate &&
      entryType === EntryType.AbEntry &&
      entryId
        ? this.updateLastContactDateIfMoreRecent(entryId, effectiveDate)
        : null;

    // Update parent if entry and parent preferences are enabled
    const updateParent =
      shouldUpdateParentLastContactDate &&
      shouldUpdateEntryLastContactDate &&
      abEntryType === EntryType.Contact &&
      parentKey
        ? this.updateLastContactDateIfMoreRecent(parentKey, effectiveDate)
        : null;

    if (!updateEntry && !updateParent) {
      return of(null);
    }

    return forkJoin({
      entry: updateEntry ?? of(null),
      parent: updateParent ?? of(null),
    }).pipe(
      map(({ entry }) => entry),
      catchError((error) => {
        console.error('Error updating last contact date:', error);
        return of(null);
      }),
    );
  }

  private updateLastContactDateIfMoreRecent(
    key: string,
    date: Date,
  ): Observable<Date | null> {
    return this.getLastContactDate(key).pipe(
      switchMap((lastContactDate) => {
        if (
          !lastContactDate ||
          lastContactDate > new Date() ||
          date > lastContactDate
        ) {
          return this.updateLastContactedDate(key, date).pipe(
            map(() => date),
            catchError(() => of(null)),
          );
        }
        return of(null);
      }),
      catchError(() => of(null)),
    );
  }

  getChildrenByParentKey(
    parentKey: string,
  ): Observable<OutlookAbEntryDetails[]> {
    const query: Octopus.Query<Octopus.AbEntry> = {
      ParentKey: { $EQ: parentKey },
    };
    const request = this.getRequest(query);
    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(map((result) => this.mapAbEntryDetailsList(result)));
  }

  getFieldOption(): Observable<AbEntryFieldOption> {
    const request: Octopus.AbEntryFieldOptionsRequest = {
      AbEntry: {
        FieldOptions: {
          'Phone/Description': [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          'Email/Description': [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          'Address/Country': [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
        },
      },
    };

    return this.http
      .post<Octopus.AbEntryFieldOptionsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new AbEntryFieldOptionMapper();
            return mapper.from(result);
          }
          return {
            phoneDescriptions: [],
            emailDescriptions: [],
            addressCountry: [],
          };
        }),
      );
  }

  private mapAbEntryDetailsList(
    result: Octopus.AbEntryResponse,
  ): OutlookAbEntryDetails[] {
    if (result.Code === Octopus.ResponseStatusCode.Successful) {
      const mapper = new AbEntryDetailsMapper();
      return result.AbEntry.Data.map((abEntry) => mapper.from(abEntry));
    }
    return [];
  }

  private getSmallRequest(
    searchQuery: Octopus.LogicalQuery<Octopus.AbEntry>,
  ): Octopus.AbEntryReadRequest {
    return {
      AbEntry: {
        Criteria: {
          SearchQuery: searchQuery,
        },
        Scope: {
          Fields: {
            Key: 1,
            FirstName: 1,
            LastName: 1,
            CompanyName: 1,
            Type: 1,
            Email1: {
              Address: 1,
              Description: 1,
              Default: 1,
            },
            Email2: {
              Address: 1,
              Description: 1,
              Default: 1,
            },
            Email3: {
              Address: 1,
              Description: 1,
              Default: 1,
            },
            LastContactDate: 1,
            ParentKey: {
              Value: 1,
            },
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
      Globalization: Octopus.DefaultGlobalization,
    };
  }

  private getRequest(
    query?: Octopus.Query<Octopus.AbEntry>,
  ): Octopus.AbEntryReadRequest {
    const searchQuery: Octopus.LogicalQuery<Octopus.AbEntry> = {
      $AND: [],
    };
    if (query) {
      searchQuery.$AND?.push(query);
    }
    return {
      AbEntry: {
        Scope: {
          Fields: {
            Key: 1,
            Phone: {
              Description: 1,
              Number: 1,
              Extension: 1,
            },
            Phone1: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Phone2: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Phone3: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Phone4: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Email: {
              Address: 1,
              Description: 1,
              Default: 1,
            },
            Email1: {
              Address: 1,
              Description: 1,
              Default: 1,
            },
            Email2: {
              Address: 1,
              Description: 1,
              Default: 1,
            },
            Email3: {
              Address: 1,
              Description: 1,
              Default: 1,
            },
            FirstName: 1,
            LastName: 1,
            CompanyName: 1,
            Position: 1,
            WebSite: 1,
            Address: {
              Key: 1,
              AddressLine1: 1,
              AddressLine2: 1,
              City: 1,
              Country: 1,
              StateProvince: 1,
              ZipCode: 1,
            },
            Parent: {
              Key: 1,
              Type: 1,
              DisplayValue: 1,
            },
            LastContactDate: {
              Value: 1,
            },
            Type: 1,
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
        OrderBy: {
          Fields: [
            {
              FirstName: 'ASC',
            },
          ],
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
      Globalization: Octopus.DefaultGlobalization,
    };
  }
}
